import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import { IMAGE_URL } from "../../config.js";
import { FaArrowLeft, FaIdCard, FaRegAddressCard, FaCreditCard } from "react-icons/fa";
import { useRoForm } from "./RoContext.js";
import { useFileUpload } from "../../Common/PreSignUrl.jsx";

const steps = [
    { label: "Personal Info", link: "/personalinformationmanagerhub" },
    { label: "Residency Address", link: "/residencyaddmanager" },
    { label: "KYC", link: "/kycmanagerhub" },
    { label: "Bank Details", link: "/bankaccountdetailsmanagerhub" },
    { label: "Agreement", link: "/aggrementdetailsmanager" },
];

const KYCManagerHUB = ({ currentStep = 3 }) => {
    const { handleFileUpload } = useFileUpload();
    const { formData, updateFormData, completedSteps, setCompletedSteps } = useRoForm();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const blankForm = {
        aadharNumber: "",
        aadharFront: "",
        aadharBack: "",
        panNumber: "",
        panFront: "",
    }
    const initialValues = { ...blankForm, ...formData };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: Yup.object({
            aadharNumber: Yup.string()
                .matches(/^\d{12}$/, "Enter a valid 12-digit Aadhaar number")
                .required("Aadhaar number is required"),
            aadharFront: Yup.mixed().required("Aadhaar front image is required"),
            aadharBack: Yup.mixed().required("Aadhaar back image is required"),
            panNumber: Yup.string()
                .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Enter a valid PAN number")
                .required("PAN number is required"),
            panFront: Yup.mixed().required("PAN card image is required"),
        }),
        onSubmit: (values) => {
            if (!completedSteps.includes('/kycmanagerhub')) {
                setCompletedSteps([...completedSteps, '/kycmanagerhub']);
            }
            updateFormData(values, '/kycmanagerhub'); // e.g., '/kycverification'


            // navigate to next step in wizard (example)
            navigate("/bankaccountdetailsmanagerhub");
            // Replace navigate with your API call to save data
            // console.log("Form submitted:", values);
        },
    });

    const handleStepClick = (link) => {
        navigate(link);
    };
    const handleFileChange = (event, keyPrefix, folderName) => {
        if ((!event?.target?.files || !event?.currentTarget?.files) || event?.target?.files?.length === 0) {
            setIsLoading(false);
            // console.log("===");
        }
        handleFileUpload({
            event,
            keyPrefix,
            setStateCallback: (url) => {
                const newUrl = url.split('/').slice(-2).join('/');
                // console.log('✌️newUrl --->', newUrl);
                if (keyPrefix === "aadharFront") {
                    formik.setFieldValue("aadharFront", newUrl);
                }
                if (keyPrefix === "aadharBack") {
                    formik.setFieldValue("aadharBack", newUrl);
                }
                if (keyPrefix === "panFront") {
                    formik.setFieldValue("panFront", newUrl);
                }
                setIsLoading(false);
            },
            setIsLoading,
            folderName
        });
    }

    return (
        <div className="px-4 py-6 bg-white rounded-lg mb-10 grid grid-cols-1">
            {/* Title */}
            <h1 className="font-bold text-gray-800 mb-7">
                Step {currentStep}: KYC Verification
            </h1>

            {/* Stepper */}
            <div className="flex justify-between pb-6 w-full overflow-x-auto whitespace-nowrap cursor-pointer items-center relative mb-4 scrollbar-hide">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className="flex-1 text-white flex flex-col items-center flex-shrink-0 min-w-[140px] relative"
                    >
                        {index !== 0 && (
                            <div className="absolute top-5 left-[-50%] w-[100%] h-0.5 bg-gray-300 z-0" />
                        )}

                        <div className="z-10" onClick={() => handleStepClick(step.link)}>
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold 
                  ${index + 1 === currentStep
                                        ? "bg-slate-900 text-white shadow-md"
                                        : "bg-white text-gray-500 border border-gray-300"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>

                        <span className="mt-2 text-sm text-center text-gray-700">
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Form */}
            <div className="bg-white md:border md:p-6 rounded-lg md:shadow-sm">
                <form onSubmit={formik.handleSubmit}>
                    <h2 className="text-lg font-bold mb-6 text-black flex items-center gap-2">
                        <FaIdCard /> KYC Details
                    </h2>

                    {/* Aadhaar Section */}
                    <h2 className="mt-4 font-bold mb-4 flex items-center gap-2">
                        <FaRegAddressCard /> Aadhaar Card Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Aadhaar Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Aadhaar Number <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="aadharNumber"

                                placeholder="Enter 12-digit Aadhaar number"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.aadharNumber}
                                className={`w-full rounded-md px-4 py-2 bg-blue-50 text-sm focus:outline-none focus:ring-2 ${formik.touched.aadharNumber && formik.errors.aadharNumber
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.aadharNumber && formik.errors.aadharNumber && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.aadharNumber}
                                </p>
                            )}
                        </div>

                        {/* Aadhaar Front */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Aadhaar Front Image <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="file"
                                name="aadharFront"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, "aadharFront", "franchiseDetails")}
                                className="w-full rounded-md px-3 py-2 bg-blue-50 text-sm border border-gray-300"
                            />
                            {formik.touched.aadharFront && formik.errors.aadharFront && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.aadharFront}
                                </p>
                            )}
                        </div>

                        {/* Aadhaar Back */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Aadhaar Back Image <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="file"
                                name="aadharBack"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, "aadharBack", "franchiseDetails")}
                                className="w-full rounded-md px-3 py-2 bg-blue-50 text-sm border border-gray-300"
                            />
                            {formik.touched.aadharBack && formik.errors.aadharBack && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.aadharBack}
                                </p>
                            )}

                        </div>
                    </div>

                    {/* PAN Section */}
                    <h2 className="mt-6 font-bold mb-4 flex items-center gap-2">
                        <FaCreditCard /> PAN Card Details
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* PAN Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                PAN Number <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="panNumber"

                                placeholder="Enter 10-character PAN number"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.panNumber}
                                className={`w-full rounded-md px-4 py-2 bg-blue-50 text-sm focus:outline-none focus:ring-2 ${formik.touched.panNumber && formik.errors.panNumber
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.panNumber && formik.errors.panNumber && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.panNumber}
                                </p>
                            )}
                        </div>

                        {/* PAN Image */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                PAN Card Image <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="file"
                                name="panFront"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, "panFront", "franchiseDetails")}
                                className="w-full rounded-md px-3 py-2 bg-blue-50 text-sm border border-gray-300"
                            />
                            {formik.touched.panFront && formik.errors.panFront && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.panFront}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-between mt-8">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                        >
                            <FaArrowLeft /> Back
                        </button>
                        <button
                            type="submit"
                            className="bg-slate-900 text-white px-6 py-2 rounded-lg hover:bg-slate-800"
                        >
                            Next
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default KYCManagerHUB;

function Page() {
    return <KYCManagerHUB currentStep={3} />;
}
